/* ==========================================================================
   FIGS embroidery — Personalize Your FIGS, shared by every prototype that
   sells an embroiderable top: the Catarina PDP, a set's kit drawer and the
   standalone page (experiments/16-embroidery/).

   Source: the claude.ai embroidery artifact (F1hsVacSPjDUHf7HJtp8U3), built
   off Figma MVP Embroidery 1622:112110 / mobile 1936:108889. Its configurator
   — four placements, text / icon / logo, the per-garment allowances, the live
   preview — is carried over rule for rule; its classes and ids are `femb-`
   namespaced. The PDP's completed state comes from the Figma PDP Embroidery
   section 1163:97123: EMBROIDERY ADDED (PDP/Embroidery - Added 1163:96298,
   Added, Expanded 2667:185680 / mobile 2667:185669) and the embroidered
   gallery plate.

     import { createFigsEmbroidery } from '/src/figs-embroidery/figs-embroidery.js';
     const emb = createFigsEmbroidery({ garment, onChange });
     emb.open(trigger);            // the configurator, over the page
     emb.card(el, { onEdit });     // Embroidery Added + its Show Summary rows
                                   // (signed in, nothing attached: the preferences)
     emb.showsCard();              // the card, not the add-on entry, belongs in the slot
     emb.preview(el, { fit });     // the live stage anywhere (a gallery plate)
     emb.setGarment(garment);      // the PDP's colour changed
     emb.total(); emb.note();      // for the bag line: "Embroidery +$96"
     emb.restore(emb.attached);    // put saved placements back (the builder)

   A garment is { src | html, frame: 'tight' | 'ghost', name, hex }: a render
   cropped to the garment (the artifact's own, a set's ghost) or the PDP's
   1200-square ghost with the garment inset.

   What's being decided:
     • Commit points. APPLY commits a placement inside the configurator; I'M
       DONE attaches the whole set of placements to the garment and closes.
       Closing any other way (×, Esc, the scrim) leaves the garment as it was.
       The ATB still adds the garment — the Figma Added frame keeps ADD.
     • Whether the shopper sees what they bought after the modal is gone: the
       card swaps to EMBROIDERY ADDED with the stage as its thumbnail (SHOW
       SUMMARY lists the charges by kind, EDIT reopens, REMOVE takes it all
       off), and the gallery gains the embroidered plate, all drawn live
       from the same placements.

   Motion is the lab's: the modal opens on the takeover recipe (fade + .985
   → 1 on --mo-slow; a sheet rising on a phone), the configurator's own
   choreography (pills FLIP between rows, the form settles in on the tight
   stagger) runs on the motion tokens, and everything goes through mo.js so
   the lab bar's Speed and Reduced reach it. Reduced: nothing travels; the
   sample mark shows finished.
   ========================================================================== */

import { animate, MO, state, track } from '../../experiments/10-pdp-prototype/mo.js';
import { isFigsSignedIn } from '../figs-nav/figs-account.js';
import './figs-embroidery.css';

/* ------------------------------------------------------------------------
   Rules — the artifact's, confirmed with the designer
   ------------------------------------------------------------------------ */

/* Chest and sleeve embroidery are priced and constrained differently:
   chest takes two lines at $10, sleeve one shorter line at $8.
   A garment allows at most 2 chest and 1 sleeve placement. */
const RULES = {
  chest:  { price: 10, lines: 2, maxChars: 26, max: 2 },
  sleeve: { price:  8, lines: 1, maxChars: 15, max: 1 },
};

const PLACEMENTS = [
  { id: 'left-chest',   label: 'Left chest',   kind: 'chest'  },
  { id: 'left-sleeve',  label: 'Left sleeve',  kind: 'sleeve' },
  { id: 'right-chest',  label: 'Right chest',  kind: 'chest'  },
  { id: 'right-sleeve', label: 'Right sleeve', kind: 'sleeve' },
];

/* The seven embroidery faces (the artifact, 2026-10-18; the files are in
   assets/fonts/). Each carries the per-tile type spec measured off the file —
   the sizes differ deliberately so the names read at the same optical weight
   across the row — and the same face stitches the garment preview. */
const FONTS = [
  { id: 'block',       label: 'Block',       stack: 'Helvetica, Arial, sans-serif',  weight: 700, size: 11, line: 16.5 },
  { id: 'script',      label: 'Script',      stack: '"Damion", cursive',             weight: 400, size: 13, line: 19.5 },
  { id: 'preston',     label: 'Preston',     stack: '"Noto Sans", sans-serif',       weight: 700, size: 11, line: 16.5 },
  /* Set larger than the file's 11px: Roboto Condensed is narrow and the
     small caps shrink its lowercase, so at 11 it read smaller than the
     other six. 13 brings it back to the same optical size. */
  { id: 'blackwell',   label: 'Blackwell',   stack: '"Roboto Condensed", sans-serif', weight: 500, size: 13, line: 19.5, smallCaps: true },
  { id: 'nightingale', label: 'Nightingale', stack: '"Bodoni Moda", serif',          weight: 700, size: 11, line: 16.5 },
  { id: 'curie',       label: 'Curie',       stack: '"Yellowtail", cursive',         weight: 400, size: 13, line: 19.5 },
  { id: 'hamilton',    label: 'Hamilton',    stack: '"Anton", sans-serif',           weight: 400, size: 11, line: 16.5 },
];

/* Hexes are exact. `prescribed` marks the three swatches carrying the RX
   pill in the design — WE PRESCRIBE is its key.
   Only "White" is named in the file; the rest are the artifact's names. */
const THREADS = [
  { id: 'black',    label: 'Black',    hex: '#2b2c30', prescribed: true },
  { id: 'white',    label: 'White',    hex: '#ffffff', prescribed: true, light: true },
  { id: 'navy',     label: 'Navy',     hex: '#2b2e43' },
  { id: 'grey',     label: 'Grey',     hex: '#8d8d96', prescribed: true },
  { id: 'slate',    label: 'Slate',    hex: '#5b7ebd' },
  { id: 'royal',    label: 'Royal',    hex: '#285ad3' },
  { id: 'maroon',   label: 'Maroon',   hex: '#612e2f' },
  { id: 'brick',    label: 'Brick',    hex: '#885157' },
  { id: 'charcoal', label: 'Charcoal', hex: '#46444c' },
  { id: 'olive',    label: 'Olive',    hex: '#56584c' },
  { id: 'forest',   label: 'Forest',   hex: '#1c3a3e' },
  { id: 'teal',     label: 'Teal',     hex: '#274e55' },
];

/* The artifact's art, exported from the file (Embroidery Icons 2026
   1622:112272 and the NBA set). Vite resolves each at build. */
const iconArt = (id) => new URL(`./assets/icons/${id}.png`, import.meta.url).href;
const nbaArt = (id) => new URL(`./assets/nba/${id}.png`, import.meta.url).href;
const icon = (id, label) => ({ id, label, src: iconArt(id) });
const team = (id, label) => ({ id, label, src: nbaArt(id) });

const ICON_SETS = {
  figs: {
    label: 'FIGS icons', price: 8,
    icons: [
      icon('heart', 'Anatomical heart'), icon('tooth', 'Tooth'),
      icon('paw', 'Paw print'),          icon('stethoscope', 'Stethoscope'),
      icon('bones', 'Bones'),            icon('beeper', 'Beeper'),
      icon('beeper-grey', 'Beeper, grey'), icon('beeper-black', 'Beeper, black'),
      icon('lungs', 'Lungs'),            icon('pill-1', 'Pill 1'),
      icon('pill-2', 'Pill 2'),          icon('pill-3', 'Pill 3'),
    ],
  },
  nba: {
    label: 'NBA icons', price: 16,
    icons: [
      team('knicks', 'Knicks'),     team('bulls', 'Bulls'),
      team('warriors', 'Warriors'), team('lakers', 'Lakers'),
      team('nuggets', 'Nuggets'),   team('celtics', 'Celtics'),
      team('heat', 'Heat'),         team('suns', 'Suns'),
      team('76ers', '76ers'),       team('spurs', 'Spurs'),
      team('bucks', 'Bucks'),       team('clippers', 'Clippers'),
      team('mavericks', 'Mavericks'), team('rockets', 'Rockets'),
      team('nets', 'Nets'),
    ],
  },
};

/* Confirmed by the designer: the digitization fee and the colourway fee are
   additive, so full colour is $60 and black & white is $40. */
const LOGO = {
  digitization: 20,
  colorways: [
    { id: 'full', label: 'Full color',      price: 40 },
    { id: 'bw',   label: 'Black and white', price: 20 },
  ],
};
const logoPrice = (id) => LOGO.digitization + LOGO.colorways.find((c) => c.id === id).price;

/* The configurator's default garment: the artifact's navy Catarina render
   (W_CATARINA_NAVY_W_O_GUIDE), as the PDP's transparent ghost of it — the
   artifact's JPEG carries a white ground that shows on a coloured plate. */
/* The navy Catarina turning in 3D on white (treed's render, 2026-10-18,
   keyed off its grey ground at 2x the 400×493 stage, front-on at its first
   frame and aligned to the ghost). A garment that carries it as `spin` shows
   it on the configurator's placement screen only. */
export const CATARINA_SPIN = {
  src: new URL('./assets/catarina-spin.mp4', import.meta.url).href,
  poster: new URL('./assets/catarina-spin-poster.webp', import.meta.url).href,
};
export const DEFAULT_GARMENT = {
  src: new URL('./assets/garment-navy.webp', import.meta.url).href,
  frame: 'ghost', name: 'Navy', hex: '#2b2e43', spin: CATARINA_SPIN,
};
const CHEVRON = new URL('./assets/chevron-down.svg', import.meta.url).href;

/* Pill trailing glyphs, drawn at the sizes measured in the file. The trash
   is exported from the file (node 2293:90757) and swaps in on hover over an
   applied pill, as a shortcut to clearing that placement. */
const GLYPH = {
  plus:  '<svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">'
         + '<path d="M4.70014 0.0217096L4.68386 3.22388C6.45862 3.34328 7.73949 3.77748 7.97829 4.72727'
         + 'L4.66757 4.71099L4.65129 8L3.27273 7.97829L3.25645 4.68928C1.49797 4.65129 0.238806 4.1791 0 3.24559'
         + 'L3.28901 3.25645L3.30529 0L4.70014 0.0217096Z" fill="currentColor"/></svg>',
  minus: '<svg width="8" height="2" viewBox="0 0 8 2" fill="none" aria-hidden="true">'
         + '<path d="M0 0.0231047L3.29796 0.034657L4.6966 0C6.47619 0.127076 7.76054 0.589169 8 1.6'
         + 'L4.68027 1.58267L3.26531 1.55957C1.50204 1.51913 0.239456 1.01661 0 0.0231047Z" fill="currentColor"/></svg>',
  check: '<svg width="12" height="9" viewBox="0 0 12 9" fill="none" aria-hidden="true">'
         + '<path d="M12 0.844727L4.46289 8.38184L0 3.91895L0.844727 3.07324L4.46289 6.69141L11.1553 0L12 0.844727Z" fill="#1EDC53"/></svg>',
  trash: '<svg class="femb-pill__trash" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">'
         + '<path d="M7.36426 0C8.13665 9.37017e-05 8.76245 0.626079 8.7627 1.39844V1.79883H12V2.84766H10.7373L10.3574 9.22559C10.2693 10.7033 9.04479 11.8572 7.56445 11.8574H4.43555C2.9552 11.8572 1.73074 10.7033 1.64258 9.22559L1.2627 2.84766H0V1.79883H3.23926V1.39844C3.2395 0.62613 3.86537 0.000174371 4.6377 0H7.36426ZM2.69043 9.16309C2.74553 10.0866 3.5104 10.8074 4.43555 10.8076H7.56445C8.4896 10.8074 9.25447 10.0866 9.30957 9.16309L9.68652 2.84863H2.31348L2.69043 9.16309ZM4.70215 9.23535H4.00293V4.42188H4.70215V9.23535ZM6.34766 9.23535H5.64844V4.42188H6.34766V9.23535ZM7.99805 9.23535H7.29883V4.42188H7.99805V9.23535ZM4.6377 1.04883C4.44484 1.049 4.28833 1.2056 4.28809 1.39844V1.79883H7.71387V1.39844C7.71363 1.20554 7.55718 1.04892 7.36426 1.04883H4.6377Z" fill="currentColor"/></svg>',
  block: '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">'
         + '<path d="M6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6'
         + 'C0 2.68629 2.68629 0 6 0ZM6 0.75C3.10051 0.75 0.75 3.10051 0.75 6C0.75 8.8995 3.10051 11.25 6 11.25'
         + 'C8.8995 11.25 11.25 8.8995 11.25 6C11.25 3.10051 8.8995 0.75 6 0.75Z" fill="currentColor"/>'
         + '<path d="M1.89234 10.0353L10.6196 2.39889" stroke-width="0.872727" stroke="currentColor"/></svg>',
};

/* The prescription pill, shared by the WE PRESCRIBE key and the swatches. */
const RX = '<svg class="femb-rx" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M6.86905 0.0139184L6.88972 0.0114119C7.55704 -0.0656707 8.41513 0.258361 8.93893 0.662941C10.053 1.52343 10.2647 3.05152 9.69164 4.28814C9.64574 4.38714 9.48145 4.57273 9.40779 4.66338C8.98875 5.17907 8.48698 5.64422 8.01037 6.10994L6.58616 7.52212C5.97325 8.13475 5.29712 8.84135 4.64827 9.41896C4.35729 9.67803 3.51252 9.95603 3.12416 9.99687C2.3746 10.0305 1.63822 9.79253 1.05113 9.32706C0.4633 8.85766 0.0912954 8.17112 0.0199628 7.42398C-0.10075 6.23796 0.330674 5.45897 1.18706 4.68829C1.23617 4.64411 1.28952 4.59214 1.33616 4.54499L2.71551 3.16526L4.15345 1.72684C4.52065 1.3561 4.983 0.852613 5.40005 0.547035C5.75185 0.289249 6.43264 0.0558138 6.86905 0.0139184ZM4.75586 2.53568L6.6147 4.40623C6.74649 4.53889 7.13788 4.87462 7.22031 5.00692C7.27993 5.08678 7.32886 5.16708 7.41894 5.20944C7.66476 5.22834 7.74783 4.92156 7.91282 4.79602C9.02225 3.95172 9.50991 2.73594 8.49384 1.58511C8.11727 1.15858 7.43478 0.908416 6.8583 1.02096C5.88401 1.11352 5.44033 1.8998 4.75586 2.53568ZM3.56343 8.87356C4.28337 8.63767 4.69661 7.96125 5.2302 7.46813C4.88888 7.08299 4.55296 6.75842 4.18845 6.3974C3.89824 6.10998 3.60415 5.83239 3.33061 5.5291C3.13877 5.31274 2.86794 5.13609 2.69412 4.89094C2.63665 4.82027 2.59778 4.78036 2.51419 4.74159C2.33541 4.86062 2.25739 5.01921 2.11221 5.1662C1.97394 5.30783 1.81169 5.41924 1.67373 5.56168C0.918156 6.34195 0.715233 7.24671 1.32531 8.18188C1.79469 8.90135 2.76486 9.16572 3.56343 8.87356ZM5.96568 6.73682C6.30226 6.5197 6.45122 6.19336 6.78008 5.93985C6.57368 5.78305 6.45265 5.59832 6.26623 5.41808L4.48091 3.6586C4.3896 3.56776 4.15639 3.28297 4.06027 3.24259C3.76695 3.42806 3.51376 3.77758 3.24739 4.03961C3.49119 4.2876 5.85355 6.73566 5.96568 6.73682Z" fill="currentColor"/></svg>';

/* ------------------------------------------------------------------------
   Pure helpers over one placement's data
   ------------------------------------------------------------------------ */
const newDraft = () => ({
  line1: '', line2: '', font: 'block', thread: 'white',
  decoration: null,    // null | 'icon' | 'logo'
  browsingSet: 'figs', // which icon tab is on screen
  icon: null,          // { set, id } — the pick, independent of the tab
  logo: null,          // { src, name, consent, colorway }
});

const placeById = (id) => PLACEMENTS.find((p) => p.id === id);
const rulesFor  = (id) => RULES[placeById(id).kind];
const hasText   = (d) => Boolean(d && (d.line1.trim() || d.line2.trim()));
const threadBy  = (id) => THREADS.find((t) => t.id === id);
// bag lines saved before the font table arrived name Font 3–7: they stitch in Block
const fontBy    = (id) => FONTS.find((f) => f.id === id) ?? FONTS[0];
const iconOf    = (d) => d.icon && ICON_SETS[d.icon.set].icons.find((i) => i.id === d.icon.id);
const clone     = (m) => new Map([...m].map(([k, v]) => [k, structuredClone(v)]));
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const money = (n) => `$${n}`;
/* Inside the configurator prices always print to two decimals (the
   artifact); the card and the bag keep Figma's whole dollars. */
const cents = (n) => n.toFixed(2);

/* A sleeve can carry text or a logo, never both. Text takes precedence: once
   a sleeve has text, Add Logo is locked until the text is cleared. The
   reverse is held by disabling the fields while a sleeve logo is attached,
   so the pair can never both be populated. */
const logoLocked = (placeId, d) => placeById(placeId).kind === 'sleeve' && hasText(d);
const textActive = (placeId, d) => !(placeById(placeId).kind === 'sleeve' && d.decoration === 'logo');

/** A decoration only counts against the allowance once it's actually chosen. */
function decorationComplete(d) {
  if (d.decoration === 'icon') return Boolean(d.icon);
  if (d.decoration === 'logo') return Boolean(d.logo?.src);
  return false;
}
/* Text is optional: a placement can be just an icon, or just a logo. Text
   that's suppressed by a sleeve logo doesn't count as content. */
const hasContent = (placeId, d) =>
  Boolean(d) && ((hasText(d) && textActive(placeId, d)) || decorationComplete(d));

/** Text price plus whichever decoration is attached. Icon and logo are exclusive. */
function priceOf(placeId, d) {
  // Each component is charged only when it's actually there — a placement
  // with just an icon costs the icon, not the icon plus a text fee.
  let total = hasText(d) && textActive(placeId, d) ? RULES[placeById(placeId).kind].price : 0;
  // Priced by the set the chosen icon belongs to, not the tab being browsed.
  if (d.decoration === 'icon' && d.icon) total += ICON_SETS[d.icon.set].price;
  if (d.decoration === 'logo' && d.logo?.src) total += logoPrice(d.logo.colorway);
  return total;
}
const sumOf = (map) => [...map].reduce((s, [id, d]) => s + priceOf(id, d), 0);

/* The signed-in shopper's saved embroidery (PDP/Embroidery - User With
   Preferences 2667:186654 / mobile 2667:186643), placed as its thumbnail
   shows them and priced by the rules: chest text ×2 $20, the logo $60, the
   sleeve text $8, the icon $8 = Figma's TOTAL: $96. A guest never sees it:
   only a shopper who has been through the nav's log-in (or sign-up) flow is
   offered it, in place of ADD EMBROIDERY. The logo is the frame's. */
const PREFERENCES = new Map([
  ['left-chest', { ...newDraft(), line1: 'Amy Jane', line2: 'Registered Nurse', decoration: 'logo',
    logo: { src: new URL('./assets/prefs-logo.webp', import.meta.url).href, name: 'Saved logo', consent: true, colorway: 'full' } }],
  ['right-chest', { ...newDraft(), line1: 'Unity Hospital' }],
  ['left-sleeve', { ...newDraft(), line1: 'Emergency' }],
  ['right-sleeve', { ...newDraft(), decoration: 'icon', icon: { set: 'figs', id: 'bones' } }],
]);

/* ------------------------------------------------------------------------
   The stage — the design's 400×493 coordinate space, four slots pinned to
   it. Used by the configurator and by every preview.
   ------------------------------------------------------------------------ */
function garmentFill(g) {
  if (g.frame === 'ghost') {
    return `<div class="femb-stage__ghost">${g.html || `<img src="${esc(g.src)}" alt="" decoding="async" draggable="false">`}</div>`;
  }
  return `<img class="femb-stage__img" src="${esc(g.src)}" alt="" decoding="async" draggable="false">`;
}

const stageHTML = (g, { alt = '' } = {}) => `
  <div class="femb-stage">
    <div class="femb-stage__garment"${alt ? ` role="img" aria-label="${esc(alt)}"` : ''} data-femb-garment>${garmentFill(g)}</div>
    ${PLACEMENTS.map((p) => `<div class="femb-mark" data-place="${p.id}"></div>`).join('')}
  </div>`;

function imageEl(src, className) {
  const img = document.createElement('img');
  img.className = className;
  img.src = src;
  img.alt = '';
  return img;
}

/** Paint one placement's embroidery into its slot. */
function paintMark(node, placeId, data) {
  node.textContent = '';
  if (!data || !hasContent(placeId, data)) return;
  if (data.decoration === 'logo' && data.logo?.src) {
    node.append(imageEl(data.logo.src, 'femb-mark__art femb-mark__logo'));
  } else if (data.decoration === 'icon' && data.icon) {
    const ic = iconOf(data);
    if (ic) node.append(imageEl(ic.src, 'femb-mark__art'));
  }
  // Suppressed sleeve text is hidden on the garment, as the warning says.
  const lines = textActive(placeId, data)
    ? [data.line1, data.line2].slice(0, rulesFor(placeId).lines).filter((l) => l.trim())
    : [];
  for (const line of lines) {
    const span = document.createElement('span');
    span.className = 'femb-mark__line';
    const face = fontBy(data.font);
    span.style.color = threadBy(data.thread).hex;
    span.style.fontFamily = face.stack;
    span.style.fontWeight = face.weight;
    span.style.fontVariantCaps = face.smallCaps ? 'all-small-caps' : 'normal';
    span.textContent = line;
    node.append(span);
  }
}

/* ------------------------------------------------------------------------
   Embroidery as data — what a bag line carries, readable anywhere
   ------------------------------------------------------------------------ */

/** The bag's rows (and the card's summary) for a set of placements: logos,
    icons, chest text, sleeve text (the cart's order), each with its charge
    and placement. */
function linesOf(map) {
  const out = [];
  for (const p of PLACEMENTS) {
    const d = map.get(p.id);
    if (!d) continue;
    const placement = `Placement: ${p.label.replace(/\b\w/g, (c) => c.toUpperCase())}`;
    if (d.decoration === 'logo' && d.logo?.src) {
      out.push({ rank: 0, place: p.id, part: 'logo', title: 'Logo', price: logoPrice(d.logo.colorway),
        details: [d.logo.name || 'Logo', placement], art: d.logo.src });
    } else if (d.decoration === 'icon' && d.icon) {
      const ic = iconOf(d);
      out.push({ rank: 1, place: p.id, part: 'icon', title: 'Icon', price: ICON_SETS[d.icon.set].price,
        details: [ic?.label || 'Icon', placement], art: ic?.src });
    }
    if (hasText(d) && textActive(p.id, d)) {
      const text = [d.line1, d.line2].slice(0, RULES[p.kind].lines).filter((l) => l.trim());
      out.push({ rank: p.kind === 'chest' ? 2 : 3, place: p.id, part: 'text',
        title: p.kind === 'chest' ? 'Chest Text' : 'Sleeve Text', price: RULES[p.kind].price,
        details: [...text, placement], text, thread: threadBy(d.thread).hex,
        font: fontBy(d.font).stack, weight: fontBy(d.font).weight, smallCaps: Boolean(fontBy(d.font).smallCaps) });
    }
  }
  return out.sort((a, b) => a.rank - b.rank);
}

/* A garment any page can draw: its URLs made absolute (a PDP ghost's paths
   are relative to the PDP; the bag shows it on every prototype). */
function portableGarment(g) {
  const abs = (u) => { try { return new URL(u, location.href).href; } catch { return u; } };
  const out = { frame: g.frame, name: g.name, hex: g.hex };
  if (g.src) out.src = abs(g.src);
  if (g.html) {
    const t = document.createElement('template');
    t.innerHTML = g.html;
    t.content.querySelectorAll('img[src]').forEach((i) => i.setAttribute('src', abs(i.getAttribute('src'))));
    t.content.querySelectorAll('[style]').forEach((n) => n.setAttribute('style',
      n.getAttribute('style').replace(/url\((['"]?)([^'")]+)\1\)/g, (m, q, u) => `url(${q}${abs(u)}${q})`)));
    out.html = t.innerHTML;
  }
  return out;
}

/* The bag line's embroidery: the charges (rows to list without this module),
   the total, the garment to draw them on, and the placements themselves so
   the bag's EDIT can reopen them in the configurator. */
function payloadOf(map, garment) {
  return {
    total: sumOf(map),
    garment,
    placements: [...map].map(([k, v]) => [k, structuredClone(v)]),
    items: linesOf(map).map(({ rank, ...l }) => l),
  };
}

/** One charge off a bag payload (the bag's REMOVE); null when none are left. */
export function embroideryWithout(payload, place, part) {
  const map = new Map(payload.placements.map(([k, v]) => [k, structuredClone(v)]));
  const d = map.get(place);
  if (!d) return payload;
  if (part === 'text') { d.line1 = ''; d.line2 = ''; }
  else { d.decoration = null; if (part === 'icon') d.icon = null; else d.logo = null; }
  if (!hasContent(place, d)) map.delete(place);
  return map.size ? payloadOf(map, payload.garment) : null;
}

/** Draw a bag payload's embroidered garment in `host` (no configurator
    needed): the stage, fitted so the garment spans `fit` of the width. */
export function renderEmbroideryPreview(host, { garment, placements = [], fit = 1 }) {
  host.classList.add('femb-preview');
  host.innerHTML = stageHTML(garment);
  const stage = host.querySelector('.femb-stage');
  const map = new Map(placements);
  stage.querySelectorAll('.femb-mark').forEach((n) => paintMark(n, n.dataset.place, map.get(n.dataset.place)));
  const ro = new ResizeObserver(() => fitNow());
  function fitNow() {
    if (!host.isConnected) { ro.disconnect(); return; }
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return;
    const sc = Math.min((w * fit) / 400, h / 493);
    stage.style.transformOrigin = '0 0';
    stage.style.transform = `translate(${(w - 400 * sc) / 2}px, ${(h - 493 * sc) / 2}px) scale(${sc})`;
  }
  ro.observe(host);
  fitNow();
}

/* An uploaded logo is kept as a small copy (it's drawn at 32px on the
   garment and 56px in the bag), so a bag line fits in localStorage. */
function smallLogo(dataUrl, max = 240) {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.naturalWidth * k));
      c.height = Math.max(1, Math.round(img.naturalHeight * k));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      try { res(c.toDataURL('image/webp', 0.86)); } catch { res(dataUrl); }
    };
    img.onerror = () => res(dataUrl);
    img.src = dataUrl;
  });
}

/* ------------------------------------------------------------------------
   The modal's markup — the artifact's, namespaced
   ------------------------------------------------------------------------ */
const MODAL_HTML = (g) => `
<div class="femb" id="femb" hidden>
  <div class="femb-scrim" data-femb-scrim aria-hidden="true"></div>
  <div class="femb-frame" data-femb-frame>
  <div class="femb-modal" role="dialog" aria-modal="true" aria-labelledby="femb-title" tabindex="-1" data-femb-modal>

    <!-- Left column: live preview -->
    <section class="femb-left" aria-label="Live preview">
      <div class="femb-left__top">
        <h2 class="femb-title" id="femb-title">Personalize Your FIGS</h2>
        <p class="femb-subtitle">with text, icon, and logo embroidery</p>
      </div>

      <p class="femb-live"><span class="femb-live__dot"></span><span>Live preview</span></p>

      <!-- The stage is always 400×493 internally and scaled to fit, so every
           slot coordinate stays in the design's own coordinate space. -->
      <div class="femb-stage-wrap" data-femb-stage-wrap>${stageHTML(g, { alt: `${g.name} scrub top` })}</div>

      <p class="femb-colorway"><span class="femb-colorway__chip" data-femb-chip></span><span data-femb-colorway></span></p>
    </section>

    <!-- Right column: controls -->
    <section class="femb-middle" aria-label="Embroidery options">

      <!-- Pinned while a placement is open: only the form below it scrolls. -->
      <div class="femb-pills" id="femb-pillsTop"></div>

      <div class="femb-middle__scroll" id="femb-panelScroll">
        <div class="femb-middle__inner">

          <!-- Collapsed: nothing open -->
          <div class="femb-collapsed" id="femb-emptyState">
            <!-- The icon cycles through the FIGS set and the name types itself
                 in; both are decorative, so the text is exposed to assistive
                 tech once via aria-label and the animated spans are hidden. -->
            <div class="femb-sample" id="femb-emptySample">
              <div class="femb-sample__icon" id="femb-sampleIcon">
                <img src="${iconArt('tooth')}" alt="" width="64" height="64">
                <img alt="" width="64" height="64" hidden>
              </div>
              <p class="femb-sample__lines" aria-label="Amy Jane, Registered Nurse">
                <span class="femb-sample__line" data-line="0" data-text="Amy Jane" aria-hidden="true"></span>
                <span class="femb-sample__line" data-line="1" data-text="Registered Nurse" aria-hidden="true"></span>
              </p>
            </div>
            <p class="femb-prompt">Let’s pick your placements</p>
            <div class="femb-pills" id="femb-pillsCenter"></div>
            <p class="femb-limit" id="femb-limitMsg" hidden>Embroidery limit reached</p>
          </div>

          <!-- Open: the configuration form -->
          <form class="femb-editor" id="femb-editor" hidden>

            <div class="femb-row femb-row--16" id="femb-textRow">
              <div class="femb-label-row">
                <p class="femb-label">Add Text — <span class="femb-price">$<span id="femb-addTextPrice">10.00</span></span></p>
                <button class="femb-clear" type="button" id="femb-clearText" hidden>Clear</button>
              </div>
              <div class="femb-forms">
                <div class="femb-field">
                  <input class="femb-field__input" id="femb-line1" type="text" maxlength="26"
                         placeholder="First Line" autocomplete="off" spellcheck="false" aria-label="Embroidery text, first line">
                  <span class="femb-field__count" id="femb-count1" hidden></span>
                </div>
                <div class="femb-field" id="femb-field2">
                  <input class="femb-field__input" id="femb-line2" type="text" maxlength="26"
                         placeholder="Second Line (Optional)" autocomplete="off" spellcheck="false" aria-label="Embroidery text, second line">
                  <span class="femb-field__count" id="femb-count2" hidden></span>
                </div>
              </div>
            </div>

            <div class="femb-row femb-row--24">
              <p class="femb-label">Font — <span id="femb-fontName">Block</span></p>
              <div class="femb-variants" id="femb-fontTiles" role="radiogroup" aria-label="Embroidery font"></div>
            </div>

            <!-- Three siblings: desktop parks the key on the label's line, mobile
                 lets it fall through as the third row. -->
            <div class="femb-row femb-row--24 femb-thread-row">
              <p class="femb-label">Thread Color — <span id="femb-threadName">White</span></p>
              <div class="femb-swatches" id="femb-threadRow" role="radiogroup" aria-label="Thread color"></div>
              <span class="femb-prescribed">${RX}<span>We prescribe</span></span>
            </div>

            <!-- Decoration: icon and logo are mutually exclusive -->
            <div class="femb-row femb-row--24">
              <div class="femb-addons">
                <label class="femb-addon" id="femb-addonIcon">
                  <input type="radio" name="femb-addon" value="icon" id="femb-addonIconInput">
                  <span class="femb-label" id="femb-addonIconLabel">Add Icon</span>
                </label>
                <label class="femb-addon" id="femb-addonLogo">
                  <input type="radio" name="femb-addon" value="logo" id="femb-addonLogoInput">
                  <span class="femb-label" id="femb-addonLogoLabel">Add Logo</span>
                </label>
              </div>

              <!-- Sleeves can't carry both; shown when a logo displaces the text. -->
              <p class="femb-note" id="femb-sleeveNote" role="status" hidden>Logo is unavailable
                while text is added to sleeve placements.</p>

              <div class="femb-toggles" id="femb-iconTabs" role="tablist" aria-label="Icon set" hidden></div>
              <div class="femb-icon-grid" id="femb-iconGrid" role="radiogroup" aria-label="Icon" hidden></div>
            </div>

            <!-- Logo sub-flow -->
            <div class="femb-row femb-row--24" id="femb-logoBlock" hidden>
              <p class="femb-label">Logo Digitization — <span class="femb-price">$<span>${cents(LOGO.digitization)}</span></span></p>
              <p class="femb-help">This is a one-time digitization setup fee for new logos.</p>
              <div class="femb-addons femb-addons--stack" id="femb-logoColorways" role="radiogroup" aria-label="Logo digitization"></div>

              <div class="femb-drop" id="femb-dropzone" tabindex="0" role="button"
                   aria-label="Drag and drop a logo, or browse to upload">
                <!-- Exported from the file (node 1877:98864) rather than redrawn. -->
                <svg class="femb-drop__glyph" viewBox="0 0 17 17" fill="none" aria-hidden="true">
                  <path d="M3.21566 0.35418V4.25001C3.21566 4.82139 3.50135 5.10709 4.07274 5.10709H9.5269C10.103 5.10709 10.3911 4.82139 10.3911 4.25001V0.35418M3.21566 16.6458V10.6958C3.21566 10.115 3.50607 9.82458 4.0869 9.82458H12.8986C13.4841 9.82458 13.7769 10.115 13.7769 10.6958V16.6458M1.77066 0.35418H11.7581C12.1831 0.35418 12.5491 0.507652 12.8561 0.814596L16.1852 4.14376C16.4922 4.4507 16.6456 4.81667 16.6456 5.24167V15.2292C16.6456 16.1736 16.1734 16.6458 15.229 16.6458H1.77066C0.826215 16.6458 0.353993 16.1736 0.353993 15.2292V1.77084C0.353993 0.826401 0.826215 0.35418 1.77066 0.35418Z"
                        stroke="currentColor" stroke-width="0.708332" stroke-linejoin="round"/>
                </svg>
                <p class="femb-drop__lead">
                  <span class="femb-drop__wide">Drag &amp; drop<br>or browse your computer to upload</span>
                  <span class="femb-drop__narrow">Upload a photo</span>
                </p>
                <input type="file" id="femb-logoFile" accept="image/*" hidden>
              </div>

              <div class="femb-asset" id="femb-assetCard" hidden>
                <button class="femb-asset__remove" type="button" id="femb-removeLogo">Remove</button>
                <img class="femb-asset__img" id="femb-assetImg" alt="Uploaded logo">
              </div>

              <div class="femb-field" id="femb-logoNameField" hidden>
                <input class="femb-field__input" id="femb-logoName" type="text" maxlength="40"
                       placeholder="Logo Name" autocomplete="off" spellcheck="false" aria-label="Logo name">
              </div>

              <label class="femb-consent" id="femb-consentRow" hidden>
                <input type="checkbox" id="femb-logoConsent">
                <span>By submitting this logo for embroidery you acknowledge that you have the rights to
                  use this logo for garments and agree with our
                  <a href="#" class="femb-consent__link">Terms &amp; Conditions</a>.</span>
              </label>
            </div>

          </form>

        </div>
      </div>

      <!-- Pinned to the bottom of the column: the primary action and the bag
           link stay reachable however short the viewport is, and only the form
           fields scroll between here and the pill row. The apply button is
           associated back to the form so Enter still submits from a field. -->
      <div class="femb-middle__footer">
        <div class="femb-buttons" id="femb-buttonRow" hidden>
          <button class="femb-btn femb-btn--ghost" type="button" id="femb-resetBtn">Reset</button>
          <button class="femb-btn femb-btn--solid" type="submit" form="femb-editor" id="femb-applyBtn" disabled>
            Apply to&nbsp;<span id="femb-applyTarget">left chest</span>&nbsp;— <span class="femb-price">$<span id="femb-applyPrice">10.00</span></span>
          </button>
        </div>

        <button class="femb-done" type="button" id="femb-doneBtn" hidden>
          I’m Done — Add $<span id="femb-doneTotal">0.00</span> to bag
        </button>
      </div>

    </section>

    <button class="femb-close" type="button" aria-label="Close personalization" data-femb-close>
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <path d="M1 1l18 18M19 1L1 19" stroke="currentColor" stroke-width="1.7" fill="none"/>
      </svg>
    </button>
  </div>
  </div>
</div>`;

/* ------------------------------------------------------------------------
   Motion — the artifact's choreography on the lab's tokens and bridge
   ------------------------------------------------------------------------ */
const stillness = matchMedia('(prefers-reduced-motion: reduce)');
const reduced = () => state.reduced || stillness.matches;
/* Mobile lays the column out differently enough that render() has to know. */
const compact = matchMedia('(max-width: 779px)');

/* One controller per node: rapidly switching placements would otherwise
   stack animations that compose into a wrong offset. */
const running = new WeakMap();
function move(node, kf, opts) {
  running.get(node)?.stop?.();
  const c = track(animate(node, kf, opts));
  running.set(node, c);
  return c;
}

/* ------------------------------------------------------------------------
   swapSlot — the page's add-on entry and EMBROIDERY ADDED trading places,
   or the card arriving in / leaving an empty slot (the set page's form).
   The incoming element grows from the outgoing one's height to its own
   (or from nothing, taking its share of the parent's gap with it) while it
   fades in, so what's below moves once, smoothly, on the standard curve.
   Pass null for either side.
   ------------------------------------------------------------------------ */
export async function swapSlot(from, to) {
  const shown = (n) => n && !n.hidden;
  if (!shown(from) && shown(to)) return;
  const gapOf = (n) => parseFloat(getComputedStyle(n.parentElement).rowGap) || 0;
  const h0 = shown(from) ? from.getBoundingClientRect().height : 0;
  if (to) {
    if (from) from.hidden = true;
    to.hidden = false;
    if (reduced()) return;
    const h1 = to.getBoundingClientRect().height;
    const kf = { height: [h0, h1], opacity: [0, 1] };
    if (!h0) kf.marginTop = [-gapOf(to), 0];
    /* pin the start in the same task: motion's first height frame lands a
       frame late, and without the pin that frame paints the full height */
    to.style.overflow = 'clip';
    to.style.height = `${h0}px`;
    to.style.opacity = '0';
    if (!h0) to.style.marginTop = `${-gapOf(to)}px`;
    await move(to, kf, { duration: MO.slow, ease: MO.eStandard })?.finished;
    // opacity stays explicit: a page's reveal pre-hide (.mo-pre) would
    // otherwise take a cleared element back to 0
    requestAnimationFrame(() => { to.style.height = ''; to.style.marginTop = ''; to.style.overflow = ''; to.style.opacity = '1'; });
  } else if (shown(from)) {
    if (!reduced()) {
      from.style.overflow = 'clip';
      from.style.height = `${h0}px`;
      await move(from, { height: [h0, 0], marginTop: [0, -gapOf(from)], opacity: [1, 0] },
        { duration: MO.base, ease: MO.eStandard })?.finished;
    }
    from.hidden = true;
    requestAnimationFrame(() => { from.style.height = ''; from.style.marginTop = ''; from.style.overflow = ''; from.style.opacity = ''; });
  }
}

/* ------------------------------------------------------------------------
   createFigsEmbroidery — one configurator per page
   ------------------------------------------------------------------------ */
let instance = null;

export function createFigsEmbroidery({ garment = DEFAULT_GARMENT, onChange = () => {} } = {}) {
  if (instance) return instance;

  let g = garment;
  /** Attached to the garment — what I'M DONE committed. */
  let attached = new Map();

  /* The configurator's working copy, as the artifact keeps it: committed
     placements, binned ones (reopening restores their inputs; only RESET
     wipes), and the placement being edited with its uncommitted draft. */
  let applied = new Map();
  const stashed = new Map();
  let openId = null;
  let draft = null;

  document.body.insertAdjacentHTML('beforeend', MODAL_HTML(g));
  const root = document.getElementById('femb');
  const frame = root.querySelector('[data-femb-frame]');
  const modal = root.querySelector('[data-femb-modal]');
  const scrim = root.querySelector('[data-femb-scrim]');

  /* Only the configurator's stage carries the spin and the landing sample;
     previews never do. The spin sits over the ghost, under the marks. */
  root.querySelector('[data-femb-stage-wrap] .femb-stage__garment').insertAdjacentHTML('afterend', `
    <div class="femb-stage__spin" aria-hidden="true">
      <video muted playsinline disablepictureinpicture preload="none" width="400" height="493"></video>
    </div>`);
  /* Phones hide the right column's sample, so the landing sample plays on the
     garment itself, in the left-chest slot (the artifact's mobile). */
  root.querySelector('[data-femb-stage-wrap] .femb-stage').insertAdjacentHTML('beforeend', `
    <div class="femb-mark femb-mark--demo" data-place="left-chest" id="femb-garmentDemo" aria-hidden="true" hidden>
      <img class="femb-mark__art" id="femb-demoIcon" src="${iconArt('tooth')}" alt="">
      <span class="femb-mark__line femb-sample__line" data-line="0" data-text="Amy Jane"></span>
      <span class="femb-mark__line femb-sample__line" data-line="1" data-text="Registered Nurse"></span>
    </div>`);

  const el = Object.fromEntries([
    'pillsTop', 'pillsCenter', 'emptyState', 'emptySample', 'limitMsg', 'editor',
    'line1', 'line2', 'field2', 'count1', 'count2', 'clearText', 'addTextPrice', 'applyPrice',
    'fontTiles', 'fontName', 'threadRow', 'threadName',
    'addonIcon', 'addonLogo', 'addonIconInput', 'addonLogoInput',
    'addonIconLabel', 'addonLogoLabel', 'iconTabs', 'iconGrid',
    'textRow', 'sleeveNote',
    'logoBlock', 'logoColorways', 'dropzone', 'logoFile', 'assetCard', 'assetImg',
    'removeLogo', 'logoNameField', 'logoName', 'consentRow', 'logoConsent',
    'applyBtn', 'applyTarget', 'resetBtn', 'buttonRow', 'doneBtn', 'doneTotal',
    'panelScroll', 'sampleIcon', 'garmentDemo', 'demoIcon',
  ].map((id) => [id, document.getElementById(`femb-${id}`)]));

  const stageWrap = root.querySelector('[data-femb-stage-wrap]');
  const stageEl = stageWrap.querySelector('.femb-stage');
  const marks = Object.fromEntries([...stageEl.querySelectorAll('.femb-mark:not(.femb-mark--demo)')].map((n) => [n.dataset.place, n]));

  /* -- Allowances (over the working copy) ------------------------------- */

  /** Committed placements of this kind that actually carry text. */
  const textCountOfKind = (kind, exceptId) =>
    [...applied].filter(([id, d]) =>
      id !== exceptId && placeById(id).kind === kind && hasText(d) && textActive(id, d)).length;

  /** Is a decoration of this type committed somewhere other than `exceptId`? */
  const decorationUsed = (type, exceptId) =>
    [...applied].some(([id, d]) => id !== exceptId && d.decoration === type && decorationComplete(d));

  /**
   * What could still be added to a placement. The caps are per component, not
   * per placement: text is 2 chest + 1 sleeve, icon and logo are 1 each across
   * the whole garment. So a sleeve whose text slot is spent is still open for
   * a logo.
   */
  function optionsFor(id) {
    const { kind } = placeById(id);
    return {
      text: textCountOfKind(kind, id) < RULES[kind].max,
      icon: !decorationUsed('icon', id),
      logo: !decorationUsed('logo', id),
    };
  }

  /** Unavailable only when nothing at all could be added to it. */
  function isLocked(id) {
    if (applied.has(id)) return false;
    const o = optionsFor(id);
    return !o.text && !o.icon && !o.logo;
  }

  /* Nothing left to add anywhere — every placement is applied or unavailable. */
  const atLimit = () => PLACEMENTS.every((p) => applied.has(p.id) || isLocked(p.id));

  /**
   * The notice under the pill row. Two cases:
   *  - the whole garment is spent        → "Embroidery limit reached"
   *  - only some placements are spent    → name them, so a greyed-out pill
   *                                        beside a live one is explained
   * Returns null when everything is still open.
   */
  function limitNotice() {
    const locked = PLACEMENTS.filter((p) => isLocked(p.id));
    if (!locked.length) return null;
    if (atLimit()) return 'Embroidery limit reached';
    const names = locked.map((p) => p.label.toLowerCase());
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
    return `No embroidery options remain for ${list}`;
  }

  /** The done link counts committed placements plus the open one, as the design does. */
  function cartTotal() {
    let sum = sumOf(applied);
    if (openId && hasContent(openId, draft)) sum += priceOf(openId, draft);
    return sum;
  }

  /** Everything that must be true before a placement can be committed. */
  function canApply() {
    if (!openId || !hasContent(openId, draft)) return false;
    // A logo requires a name and an accepted rights statement.
    if (draft.decoration === 'logo' && draft.logo?.src) {
      if (!draft.logo.name.trim() || !draft.logo.consent) return false;
    }
    return true;
  }

  /* -- Fit: the stage to its column, the modal to the viewport ---------- */

  function fitStage() {
    const { clientWidth: w, clientHeight: h } = stageWrap;
    if (!w || !h) return;
    // Height matters too: on a viewport shorter than the 840 design the column
    // is squeezed, and scaling on width alone would push the colourway badge
    // out through the bottom of the modal.
    const scale = Math.min(w / 400, h / 493);
    stageEl.style.setProperty('--femb-stage-scale', scale);
    stageEl.style.setProperty('--femb-stage-offset', `${(w - 400 * scale) / 2}px`);
  }

  /* The modal is a fixed 1208×840 surface. When the viewport can't hold it,
     scale the whole thing down rather than letting the right column scroll its
     content out of sight. */
  const DESIGN = { w: 1208, h: 840, margin: 32 };
  function fitModal() {
    if (compact.matches) { frame.style.removeProperty('--femb-modal-scale'); return; }
    const scale = Math.min(1, (innerWidth - DESIGN.margin) / DESIGN.w, (innerHeight - DESIGN.margin) / DESIGN.h);
    frame.style.setProperty('--femb-modal-scale', scale.toFixed(4));
  }

  // Held in a binding: an observer with no reference can be collected.
  const stageObserver = new ResizeObserver(fitStage);
  stageObserver.observe(stageWrap);
  addEventListener('resize', () => { if (!root.hidden) { fitModal(); fitStage(); } });

  /* -- Build controls --------------------------------------------------- */

  const pills = new Map();
  for (const p of PLACEMENTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.id = p.id;
    btn.innerHTML = `<span>${p.label}</span><span class="femb-pill__sign"></span>`;
    btn.addEventListener('click', (e) => {
      // The trailing glyph is a remove shortcut once the placement is applied.
      if (applied.has(p.id) && e.target.closest('.femb-pill__sign')) {
        removePlacement(p.id);
        return;
      }
      togglePlacement(p.id);
    });
    pills.set(p.id, btn);
  }

  for (const f of FONTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'femb-variant';
    btn.dataset.id = f.id;
    btn.innerHTML = `<span>${f.label}</span>`;
    // Each tile is set in its own face, at the size the file gives it.
    Object.assign(btn.style, {
      fontFamily: f.stack, fontWeight: f.weight, fontSize: `${f.size}px`, lineHeight: `${f.line}px`,
      fontVariantCaps: f.smallCaps ? 'all-small-caps' : 'normal',
    });
    btn.setAttribute('role', 'radio');
    btn.addEventListener('click', () => { draft.font = f.id; render(); });
    el.fontTiles.append(btn);
  }

  for (const t of THREADS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'femb-swatch';
    if (t.prescribed) btn.classList.add('femb-swatch--prescribed');
    if (t.light) btn.classList.add('femb-swatch--light');
    btn.dataset.id = t.id;
    btn.style.setProperty('--femb-sw', t.hex);
    btn.innerHTML = (t.prescribed ? RX : '') + '<span class="femb-swatch__color"></span>';
    btn.title = t.prescribed ? `${t.label} (prescribed)` : t.label;
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-label', btn.title);
    btn.addEventListener('click', () => { draft.thread = t.id; render(); });
    el.threadRow.append(btn);
  }

  for (const [key, set] of Object.entries(ICON_SETS)) {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'femb-toggle';
    tab.dataset.set = key;
    tab.innerHTML = `${set.label} — <span class="femb-price">$${cents(set.price)}</span>`;
    tab.setAttribute('role', 'tab');
    // Switching tabs browses a different set; it must not clear the pick or reprice.
    tab.addEventListener('click', () => { draft.browsingSet = key; render(); });
    el.iconTabs.append(tab);
  }

  for (const c of LOGO.colorways) {
    const label = document.createElement('label');
    label.className = 'femb-addon';
    label.innerHTML = `<input type="radio" name="femb-colorway" value="${c.id}">`
      + `<span class="femb-opt-label">${c.label} — <span class="femb-price">$${cents(c.price)}</span></span>`;
    label.querySelector('input').addEventListener('change', () => {
      draft.logo.colorway = c.id;
      render();
    });
    el.logoColorways.append(label);
  }

  /* -- Flow -------------------------------------------------------------- */

  function togglePlacement(id) {
    if (collapsing) return; // the fold owns the form until it commits
    // Clicking the open placement closes it, discarding the draft — the form
    // folds away first. Opening one is immediate so the fields are there to
    // scroll to.
    if (openId === id) {
      collapseThen(() => {
        openId = null;
        draft = null;
        seenFields.clear();
      });
      return;
    }
    withTransition(() => {
      openId = id;
      // Prefer what's applied, then anything binned earlier, then a blank draft.
      draft = structuredClone(applied.get(openId) ?? stashed.get(openId) ?? newDraft());
      // A different placement reuses the same inputs, so clear the record to
      // make them read as newly arrived and take the caret.
      seenFields.clear();
    });
    scrollFormIntoView();
  }

  /**
   * Un-apply a placement straight from its pill: it stops rendering on the
   * garment and gives its allowance back, but the inputs are kept so the user
   * can reopen the pill and pick up where they left off.
   */
  function removePlacement(id) {
    withTransition(() => {
      const kept = applied.get(id) ?? (openId === id ? draft : null);
      if (kept) stashed.set(id, structuredClone(kept));
      applied.delete(id);
      if (openId === id) { openId = null; draft = null; }
    });
  }

  function applyDraft() {
    if (!canApply()) return;
    const id = openId;
    const committed = structuredClone(draft);
    collapseThen(() => {
      applied.set(id, committed);
      stashed.delete(id);
      openId = null;
      draft = null;
    });
  }

  function resetDraft() {
    if (!openId) return;
    withTransition(() => {
      // RESET is the destructive one — it drops the stash too.
      applied.delete(openId);
      stashed.delete(openId);
      draft = newDraft();
      seenFields.clear();
    });
  }

  /* -- Transitions --------------------------------------------------------- */

  /** Where each pill sits right now, for the FLIP below. */
  const pillRects = () => new Map([...pills].map(([id, btn]) => [id, btn.getBoundingClientRect()]));

  /**
   * Mutate state, re-render, then animate the difference. The pills move
   * between two containers, so they're FLIPped from their old position
   * rather than jumping.
   */
  function withTransition(mutate) {
    const before = pillRects();
    const wasEditing = openId !== null;
    mutate();
    render();
    if (reduced()) return;
    flipPills(before);
    if (openId !== null) revealEditor({ expanding: !wasEditing });
    else revealCollapsed();
  }

  function flipPills(before) {
    for (const [id, btn] of pills) {
      const a = before.get(id);
      const b = btn.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      if (!dx && !dy) continue;
      move(btn, { x: [dx, 0], y: [dy, 0] }, { duration: MO.slow, ease: MO.eEntrance });
    }
  }

  /** Opening from the collapsed state expands; switching placements just settles. */
  function revealEditor({ expanding }) {
    // The action row sits in the pinned footer, but reveals with the form.
    const parts = [...el.editor.children, el.buttonRow].filter((n) => !n.hidden);
    parts.forEach((part, i) => {
      move(part, { opacity: [0, 1], y: [expanding ? MO.riseXs + 4 : 4, 0] }, {
        duration: expanding ? MO.base : MO.quick,
        delay: expanding ? 0.09 + i * MO.stagTight : 0,
        ease: MO.eEntrance,
      });
    });
  }

  function revealCollapsed() {
    for (const part of [el.emptySample, el.limitMsg, el.doneBtn]) {
      if (part.hidden) continue;
      move(part, { opacity: [0, 1] }, { duration: MO.base, delay: 0.08, ease: MO.eEntrance });
    }
  }

  /** The scroller: the right column on desktop, the whole sheet on a phone. */
  const scroller = () => (compact.matches ? frame : el.panelScroll);

  /**
   * Bring content that just appeared into view. Scrolls only as far as needed,
   * and never so far that the top of the new block leaves the viewport — if it's
   * taller than the column, you land on its start rather than its end.
   */
  function scrollRevealed(...nodes) {
    const box = scroller();
    const visible = nodes.filter((n) => n && !n.hidden);
    if (!visible.length) return;
    const pad = 16;
    const f = box.getBoundingClientRect();
    const top = Math.min(...visible.map((n) => n.getBoundingClientRect().top));
    const bottom = Math.max(...visible.map((n) => n.getBoundingClientRect().bottom));
    const below = bottom - f.bottom + pad;
    const above = f.top - top + pad;
    let delta = 0;
    if (below > 0) delta = Math.min(below, top - f.top - pad);
    else if (above > 0) delta = -above;
    if (Math.abs(delta) < 2) return;
    box.scrollBy({ top: delta, behavior: reduced() ? 'auto' : 'smooth' });
  }

  /**
   * Drop any animation still on a node and the inline geometry it was
   * driving. A height animation that never settles (a backgrounded tab stalls
   * the timeline) would otherwise keep the form pinned and reopen it empty.
   */
  function releaseGeometry(node) {
    for (const a of node.getAnimations()) a.cancel();
    for (const prop of ['height', 'overflow', 'opacity']) node.style.removeProperty(prop);
  }

  /* Phone: glide back up to the garment. Kicked off *before* the form folds:
     once it has, the sheet is shorter, the scroll is already clamped and
     there's nothing left to animate. */
  function scrollPreviewIntoView() {
    if (!compact.matches || frame.scrollTop <= 1) return;
    frame.scrollTo({ top: 0, behavior: reduced() ? 'auto' : 'smooth' });
  }

  /* Phone: the form opens below the garment, well under the fold. Nudge the
     sheet so the pill row sits just inside the top and the fields and font
     tiles land on screen. Only ever downward. (Desktop scrolls the right
     column instead, via scrollRevealed.) */
  function scrollFormIntoView() {
    if (!compact.matches || openId === null) return;
    const pad = 16;
    const target = frame.scrollTop + el.pillsCenter.getBoundingClientRect().top - frame.getBoundingClientRect().top - pad;
    if (target <= frame.scrollTop + 1) return;
    frame.scrollTo({ top: target, behavior: reduced() ? 'auto' : 'smooth' });
  }

  /**
   * Fold the open form shut before committing a close (APPLY, or tapping the
   * open pill), so on a phone the sheet folds up instead of the content
   * vanishing under the shopper: the glide back up leads by 140ms, then the
   * form clips to nothing over 220 while the whole panel fades out in the
   * first 45% — a panel shutting, not rows winking out one by one. The commit
   * runs on a timer, not the animation's promise, so a stalled timeline in a
   * background tab can't leave it half-applied. The fold is plain WAAPI with
   * no fill, as the artifact's: motion commits its end (height 0) inline as
   * it finishes, a beat after the commit, and the next open came up folded
   * shut. Desktop closes as it did: the pill FLIP and the row reveal carry it.
   */
  let collapsing = false;
  async function collapseThen(mutate) {
    if (collapsing) return; // repeat taps mid-fold
    const node = el.editor;
    releaseGeometry(node);
    const height = node.getBoundingClientRect().height;
    const commit = () => { releaseGeometry(node); withTransition(mutate); };
    if (!compact.matches) { commit(); return; }
    scrollPreviewIntoView();
    if (reduced() || !height) { commit(); return; }
    const LEAD = 0.14;
    const FOLD = 0.22;
    collapsing = true;
    await wait(LEAD * 1000 / state.speed);
    node.style.overflow = 'hidden';
    node.animate([
      { height: `${height}px`, opacity: 1, offset: 0 },
      { opacity: 0, offset: 0.45 },
      { height: '0px', opacity: 0, offset: 1 },
    ], { duration: FOLD * 1000 / state.speed, easing: `cubic-bezier(${MO.eStandard.join(',')})` });
    await wait(FOLD * 1000 / state.speed);
    collapsing = false;
    commit();
  }

  /** Fade newly revealed rows in, so the accompanying scroll reads as deliberate. */
  function revealRows(...nodes) {
    if (reduced()) return;
    for (const node of nodes) {
      if (!node || node.hidden) continue;
      move(node, { opacity: [0, 1], y: [6, 0] }, { duration: MO.quick, ease: MO.eEntrance });
    }
  }

  /* Text inputs on screen at the last render, so we can spot ones that just
     arrived. Any newly revealed field gets the caret — where several appear at
     once (a chest placement's two lines) only the first does. */
  let seenFields = new Set();
  function focusFreshField() {
    if (root.hidden) return;
    const onScreen = [...root.querySelectorAll('.femb-field__input')].filter((input) => input.offsetParent !== null);
    const fresh = onScreen.filter((input) => !seenFields.has(input));
    seenFields = new Set(onScreen);
    // A phone's keyboard would cover the sheet the moment a pill is tapped;
    // there the caret waits for the shopper's own tap.
    if (fresh.length && !compact.matches) caretInto(fresh[0]);
  }
  function caretInto(input) {
    // preventScroll because focus() would otherwise jump the column instantly,
    // fighting the smooth scroll we run alongside it.
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }

  /* -- Render -------------------------------------------------------------- */

  function render() {
    const editing = openId !== null;
    renderPills(editing);

    // Mobile keeps the prompt and pills visible above the open form.
    el.emptyState.hidden = editing && !compact.matches;
    el.editor.hidden = !editing;
    // The action row lives in the pinned footer, so it's toggled separately.
    el.buttonRow.hidden = !editing;
    // The sample mark plays on the placement screen whenever it's showing,
    // placements applied or not (treed, 2026-10-19 — it used to be first-run
    // only). It sits in the collapsed block, so an open form still hides it.
    el.emptySample.hidden = false;
    // a phone plays it on the garment until something's applied (below),
    // then here in the column
    el.emptySample.classList.toggle('is-in-column', applied.size > 0);
    // The placement screen (nothing open, nothing applied) turns the 3D
    // garment, where the garment has one.
    const landing = !editing && applied.size === 0;
    syncSpin(landing);
    // Phones hide the right column's sample, so it plays on the garment —
    // only on the untouched landing state, once a spin has settled front-on.
    el.garmentDemo.hidden = !(compact.matches && landing && (!spinOn || spinSettled));
    // render() owns the sample's lifecycle: it knows the breakpoint and
    // whether either copy is actually on screen.
    const sampleOnScreen = !root.hidden && (el.emptySample.offsetParent !== null || !el.garmentDemo.hidden);
    if (sampleRunning && !sampleOnScreen) stopSample();
    else if (!sampleRunning && sampleOnScreen) startSample();
    const notice = limitNotice();
    el.limitMsg.hidden = !notice;
    if (notice) el.limitMsg.textContent = notice;

    if (editing) renderEditor();

    const total = cartTotal();
    // While a placement is open the link keeps its slot even at $0, so typing
    // the first character doesn't shove the form upward. Embroidery already
    // attached can be taken off from here too: DONE at $0 clears it.
    el.doneBtn.hidden = !editing && total === 0 && attached.size === 0;
    el.doneBtn.classList.toggle('is-ghost', total === 0 && attached.size === 0);
    el.doneTotal.textContent = cents(total);

    renderPreview();
    focusFreshField();
  }

  function renderPills(editing) {
    // Desktop moves the pills to a pinned row above the form; mobile keeps them
    // with the prompt in the collapsed block, which stays on screen throughout.
    const slot = editing && !compact.matches ? el.pillsTop : el.pillsCenter;
    for (const p of PLACEMENTS) {
      const btn = pills.get(p.id);
      const isApplied = applied.has(p.id);
      const locked = isLocked(p.id);
      const st = openId === p.id ? 'open' : isApplied ? 'added' : locked ? 'disabled' : 'default';
      btn.className = `femb-pill femb-pill--${st}`;
      btn.disabled = locked;
      btn.setAttribute('aria-pressed', String(st === 'open'));
      btn.querySelector('.femb-pill__sign').innerHTML = st === 'added'
        ? `<span class="femb-pill__check">${GLYPH.check}</span>${GLYPH.trash}`
        : { open: GLYPH.minus, disabled: GLYPH.block, default: GLYPH.plus }[st];
      btn.title = isApplied ? `Remove ${p.label.toLowerCase()} embroidery` : '';
      // Re-appending a child reinserts it, which restarts any animation on the
      // pill and read as a flicker every render: move it only when its slot changes.
      if (btn.parentElement !== slot) slot.append(btn);
    }
  }

  function renderEditor() {
    const place = placeById(openId);
    const rules = RULES[place.kind];

    el.applyTarget.textContent = place.label.toLowerCase();
    el.addTextPrice.textContent = cents(rules.price);
    el.applyPrice.textContent = cents(priceOf(openId, draft));

    // Sleeve embroidery is a single, shorter line.
    el.field2.hidden = rules.lines < 2;
    el.line1.maxLength = rules.maxChars;
    el.line2.maxLength = rules.maxChars;
    el.line1.placeholder = rules.lines < 2 ? 'One Line Only' : 'First Line';

    if (el.line1.value !== draft.line1) el.line1.value = draft.line1;
    if (el.line2.value !== draft.line2) el.line2.value = draft.line2;
    syncCounts(rules);

    // A logo on a sleeve displaces the text: the field greys out but keeps its
    // value, the garment stops showing it, and it stops being charged.
    const suppressed = !textActive(openId, draft);
    el.textRow.classList.toggle('femb-row--muted', suppressed);
    el.line1.disabled = suppressed;
    el.line2.disabled = suppressed;
    // Only offered once there's something to clear, and never while the fields
    // are greyed out by a sleeve logo.
    el.clearText.hidden = !hasText(draft) || suppressed;

    el.fontName.textContent = fontBy(draft.font).label;
    for (const tile of el.fontTiles.children) {
      const on = tile.dataset.id === draft.font;
      tile.classList.toggle('is-on', on);
      tile.setAttribute('aria-checked', String(on));
    }

    el.threadName.textContent = threadBy(draft.thread).label;
    for (const sw of el.threadRow.children) {
      const on = sw.dataset.id === draft.thread;
      sw.classList.toggle('is-on', on);
      sw.setAttribute('aria-checked', String(on));
    }

    syncDecoration();
    el.applyBtn.disabled = !canApply();
    // Reset is drawn muted until there is something to clear.
    el.resetBtn.disabled = !hasContent(openId, draft);
  }

  /** Counters only appear once a field has content. */
  function syncCounts(rules) {
    const show = (node, n) => {
      node.hidden = n === 0;
      node.textContent = `${String(n).padStart(2, '0')}/${rules.maxChars}`;
    };
    show(el.count1, draft.line1.length);
    show(el.count2, draft.line2.length);
  }

  function syncDecoration() {
    const iconUsed = decorationUsed('icon', openId);
    const logoUsed = decorationUsed('logo', openId);
    // A sleeve that already has text locks the logo, and the label shortens.
    const bySleeveText = logoLocked(openId, draft);

    setAddon(el.addonIcon, el.addonIconInput, el.addonIconLabel,
      iconUsed, 'Add Icon', 'Icon — 1 Max', draft.decoration === 'icon');
    setAddon(el.addonLogo, el.addonLogoInput, el.addonLogoLabel,
      logoUsed || bySleeveText, 'Add Logo', bySleeveText ? 'Logo' : 'Logo — 1 Max', draft.decoration === 'logo');
    el.sleeveNote.hidden = !bySleeveText;

    const onIcon = draft.decoration === 'icon';
    el.iconTabs.hidden = !onIcon;
    el.iconGrid.hidden = !onIcon;
    el.logoBlock.hidden = draft.decoration !== 'logo';

    if (onIcon) syncIcons();
    if (draft.decoration === 'logo') syncLogo();
  }

  function setAddon(row, input, label, locked, freeText, lockedText, checked) {
    row.classList.toggle('is-locked', locked);
    input.disabled = locked;
    input.checked = checked;
    label.textContent = locked ? lockedText : freeText;
  }

  function syncIcons() {
    for (const tab of el.iconTabs.children) {
      const on = tab.dataset.set === draft.browsingSet;
      tab.classList.toggle('is-on', on);
      tab.setAttribute('aria-selected', String(on));
    }
    el.iconGrid.textContent = '';
    for (const ic of ICON_SETS[draft.browsingSet].icons) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'femb-icon';
      btn.dataset.id = ic.id;
      btn.title = ic.label;
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-label', ic.label);
      btn.innerHTML = `<img src="${ic.src}" alt="">`;
      // The rule only shows while browsing the set the pick came from.
      const on = draft.icon?.set === draft.browsingSet && draft.icon?.id === ic.id;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-checked', String(on));
      btn.addEventListener('click', () => {
        draft.icon = { set: draft.browsingSet, id: ic.id };
        render();
      });
      el.iconGrid.append(btn);
    }
  }

  function syncLogo() {
    if (!draft.logo) draft.logo = { src: '', name: '', consent: false, colorway: 'full' };
    const { logo } = draft;
    const uploaded = Boolean(logo.src);
    for (const input of el.logoColorways.querySelectorAll('input')) input.checked = input.value === logo.colorway;
    el.dropzone.hidden      = uploaded;
    el.assetCard.hidden     = !uploaded;
    el.logoNameField.hidden = !uploaded;
    el.consentRow.hidden    = !uploaded;
    if (uploaded) {
      el.assetImg.src = logo.src;
      if (el.logoName.value !== logo.name) el.logoName.value = logo.name;
      el.logoConsent.checked = logo.consent;
    }
  }

  /** Paints every committed placement, plus the live draft for the open one. */
  function renderPreview() {
    for (const place of PLACEMENTS) {
      paintMark(marks[place.id], place.id, place.id === openId ? draft : applied.get(place.id));
    }
  }

  /* -- Sample mark ----------------------------------------------------------
     The placement screen's example embroidery: the icon cycles slowly through
     the FIGS set while the name types itself in once. Both are decorative,
     stop as soon as the user opens a placement, and start over each time the
     placement screen comes back. */
  /* Each line exists twice — the right column's sample (desktop) and the
     garment's (phone) — so they're typed together by index. */
  const LINE_TEXTS = ['Amy Jane', 'Registered Nurse'];
  const lineGroups = LINE_TEXTS.map((_, i) => [...root.querySelectorAll(`.femb-sample__line[data-line="${i}"]`)]);
  const ICON_HOLD = 1800;   // ms each icon stays before the next swap
  const TYPE_CHAR = 55;     // ms per character
  const LINE_PAUSE = 320;   // ms between the two lines
  let iconTimer = null;
  let sampleRunning = false;
  let typeRun = 0;          // a restart abandons the typing already under way

  function cycleSampleIcon() {
    const frames = ICON_SETS.figs.icons;
    // Start from whatever the markup shows, so the first swap isn't a no-op.
    let i = Math.max(0, frames.findIndex((f) => f.id === 'tooth'));
    const [a, b] = el.sampleIcon.children;
    let front = a;
    iconTimer = setInterval(() => {
      i = (i + 1) % frames.length;
      const back = front === a ? b : a;
      back.src = frames[i].src;
      el.demoIcon.src = frames[i].src;
      // Let the new frame decode before swapping, so the swap isn't to a blank.
      back.decode?.().catch(() => {}).finally(() => {
        back.hidden = false;
        front.hidden = true;
        front = back;
      });
    }, ICON_HOLD);
  }

  async function typeSample() {
    const run = ++typeRun;
    for (const n of lineGroups.flat()) { n.textContent = ''; n.classList.remove('femb-sample__line--typing'); }
    for (const [index, nodes] of lineGroups.entries()) {
      const text = LINE_TEXTS[index];
      const last = index === lineGroups.length - 1;
      for (const n of nodes) n.classList.add('femb-sample__line--typing');
      for (let i = 1; i <= text.length; i++) {
        if (run !== typeRun) return;
        for (const n of nodes) n.textContent = text.slice(0, i);
        await wait(TYPE_CHAR / state.speed);
      }
      // The caret stays put on the final line, the way it would in a live field.
      if (last) break;
      for (const n of nodes) n.classList.remove('femb-sample__line--typing');
      await wait(LINE_PAUSE / state.speed);
    }
  }
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  function startSample() {
    if (sampleRunning) return;
    sampleRunning = true;
    if (reduced()) {
      // Reduced motion: show the finished state, no cycling and a static caret.
      lineGroups.forEach((nodes, i) => nodes.forEach((n) => {
        n.textContent = LINE_TEXTS[i];
        n.classList.toggle('femb-sample__line--typing', i === lineGroups.length - 1);
      }));
      return;
    }
    typeSample();
    cycleSampleIcon();
  }
  function stopSample() {
    sampleRunning = false;
    typeRun += 1;
    clearInterval(iconTimer);
    iconTimer = null;
  }

  /* -- Events ------------------------------------------------------------ */

  for (const [input, key] of [[el.line1, 'line1'], [el.line2, 'line2']]) {
    input.addEventListener('input', () => {
      // maxlength only guards typing and paste, so clamp anything else too.
      const rules = rulesFor(openId);
      if (input.value.length > rules.maxChars) input.value = input.value.slice(0, rules.maxChars);
      draft[key] = input.value;
      render();
    });
  }

  /** Switch the decoration on, or back off. Passing null clears it. */
  function setDecoration(kind) {
    draft.decoration = kind;
    render();
    // Whatever just appeared gets faded in and scrolled to.
    if (kind === 'icon') {
      revealRows(el.iconTabs, el.iconGrid);
      scrollRevealed(el.iconTabs, el.iconGrid);
    } else if (kind === 'logo') {
      revealRows(el.logoBlock);
      scrollRevealed(el.logoBlock);
    }
  }

  /* A radio won't deselect itself. On a radio the click event fires *before*
     the change event, so at click time `draft.decoration` still holds the
     pre-click value — clicking the option that's already on means "turn off".
     The picked icon or uploaded logo is kept, so toggling back restores it.
     The change handler only covers keyboard paths that bypass click, and is a
     no-op when the click handler already did the work. */
  function wireDecoration(input, kind) {
    input.addEventListener('click', () => setDecoration(draft.decoration === kind ? null : kind));
    input.addEventListener('change', () => { if (input.checked && draft.decoration !== kind) setDecoration(kind); });
  }
  wireDecoration(el.addonIconInput, 'icon');
  wireDecoration(el.addonLogoInput, 'logo');

  el.dropzone.addEventListener('click', () => el.logoFile.click());
  el.dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.logoFile.click(); }
  });
  el.dropzone.addEventListener('dragover', (e) => { e.preventDefault(); el.dropzone.classList.add('is-over'); });
  el.dropzone.addEventListener('dragleave', () => el.dropzone.classList.remove('is-over'));
  el.dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    el.dropzone.classList.remove('is-over');
    readLogo(e.dataTransfer.files[0]);
  });
  el.logoFile.addEventListener('change', () => readLogo(el.logoFile.files[0]));

  function readLogo(file) {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const src = await smallLogo(reader.result);
      draft.logo = { src, name: '', consent: false, colorway: draft.logo?.colorway ?? 'full' };
      // render() hands the caret to the Logo Name field as it appears.
      render();
      // The asset image carries no layout until it decodes. Measuring before
      // then sees a collapsed card and under-scrolls by its eventual height.
      try { await el.assetImg.decode(); } catch { /* broken image: scroll anyway */ }
      // Uploading swaps the dropzone for the asset card and adds the name and
      // consent rows below it — reveal and scroll to that whole second wave.
      revealRows(el.assetCard, el.logoNameField, el.consentRow);
      scrollRevealed(el.assetCard, el.logoNameField, el.consentRow);
    };
    reader.readAsDataURL(file);
  }

  el.removeLogo.addEventListener('click', () => {
    draft.logo = { src: '', name: '', consent: false, colorway: draft.logo.colorway };
    el.logoFile.value = '';
    render();
  });
  el.logoName.addEventListener('input', () => {
    draft.logo.name = el.logoName.value;
    el.applyBtn.disabled = !canApply();
  });
  el.logoConsent.addEventListener('change', () => {
    draft.logo.consent = el.logoConsent.checked;
    el.applyBtn.disabled = !canApply();
  });
  root.querySelector('.femb-consent__link').addEventListener('click', (e) => e.preventDefault());

  el.clearText.addEventListener('click', () => {
    draft.line1 = '';
    draft.line2 = '';
    render();
    caretInto(el.line1);
  });

  el.editor.addEventListener('submit', (e) => { e.preventDefault(); applyDraft(); });
  el.resetBtn.addEventListener('click', resetDraft);

  /* I'M DONE attaches what's on the garment — the committed placements, plus
     the open one when it could be applied (its price is already in the
     total the link shows) — and closes. */
  el.doneBtn.addEventListener('click', () => {
    if (openId && canApply()) applied.set(openId, structuredClone(draft));
    if (external) {
      const { garment: eg, onDone: done } = external;
      const payload = applied.size ? payloadOf(clone(applied), eg) : null;
      close();
      done?.(payload);
      return;
    }
    attached = clone(applied);
    close({ commit: true });
  });

  root.querySelector('[data-femb-close]').addEventListener('click', () => close());
  scrim.addEventListener('click', () => close());
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (openId) togglePlacement(openId);
    else close();
  });
  compact.addEventListener('change', () => { if (!root.hidden) { fitModal(); render(); } });

  /* -- Open / close: the takeover recipe (createDrawer's), a sheet on a phone
     ---------------------------------------------------------------------- */
  let isOpen = false;
  let opener = null;
  let inerted = [];
  let saved = null;

  function lockPage(on) {
    const html = document.documentElement;
    if (on) {
      // A kit drawer may already hold the page; keep its lock and stack ours.
      saved = { html: html.style.overflow, body: document.body.style.overflow };
      html.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      root.inert = false;
      inerted = [...document.body.children].filter((n) => n !== root && !n.inert && n.tagName !== 'SCRIPT'
        && !n.matches('[data-lab-bar], [data-lab-handle], .lab-grid, [data-live]'));
      inerted.forEach((n) => { n.inert = true; });
    } else {
      inerted.forEach((n) => { n.inert = false; });
      inerted = [];
      html.style.overflow = saved?.html ?? '';
      document.body.style.overflow = saved?.body ?? '';
    }
  }

  function open(trigger, from) {
    if (isOpen) return;
    isOpen = true;
    opener = trigger || document.activeElement;
    // Every visit starts from what's attached (or the bag line being edited);
    // nothing is left half-open.
    applied = clone(from || (offersPreferences() ? PREFERENCES : attached));
    stashed.clear();
    openId = null;
    draft = null;
    seenFields.clear();
    document.body.append(root); // last in the body: above anything opened since
    root.hidden = false;
    lockPage(true);
    fitModal();
    fitStage();
    frame.scrollTop = 0;
    el.panelScroll.scrollTop = 0;
    render(); // starts the landing sample

    const sheet = compact.matches;
    if (reduced()) {
      move(sheet ? frame : modal, sheet ? { opacity: [0, 1], y: 0 } : { opacity: [0, 1], scale: 1 }, { duration: MO.base, ease: MO.eStandard });
    } else if (sheet) {
      move(frame, { y: ['100%', '0%'], opacity: 1 }, { duration: MO.slow, ease: MO.eEntrance });
    } else {
      move(modal, { opacity: [0, 1], scale: [0.985, 1] }, { duration: MO.slow, ease: MO.eEntrance });
    }
    move(scrim, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard });
    modal.focus({ preventScroll: true });
  }

  function close({ commit = false } = {}) {
    if (!isOpen) return Promise.resolve();
    isOpen = false;
    stopSample();
    spinVideo.pause();
    spinOn = false;
    const sheet = compact.matches;
    const anims = [
      reduced()
        ? move(sheet ? frame : modal, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eStandard })
        : sheet
          ? move(frame, { y: ['0%', '100%'] }, { duration: MO.base, ease: MO.eExit })
          : move(modal, { opacity: [1, 0], scale: [1, 0.985] }, { duration: MO.base, ease: MO.eExit }),
      move(scrim, { opacity: [1, 0] }, { duration: MO.base, ease: MO.eStandard }),
    ];
    lockPage(false);
    opener?.focus?.({ preventScroll: true });
    if (external) { external = null; showGarment(g); } // back to the page's own garment
    if (commit) {
      syncPreviews();
      onChange(clone(attached), { source: 'done' });
    }
    return Promise.all(anims.map((a) => a?.finished)).catch(() => {}).then(() => {
      if (isOpen) return;
      root.hidden = true;
      // Hand the shell back to its stylesheet: a sheet's travel or the
      // modal's scale must not outlive a resize across the breakpoint.
      for (const n of [frame, modal]) { n.style.removeProperty('transform'); n.style.removeProperty('opacity'); }
    });
  }

  /* -- What's attached, for the host page -------------------------------- */

  /** The charges for what's attached (linesOf, above). */
  const lines = () => linesOf(attached);

  /**
   * The bag line's embroidery for what's attached — or for saved placements
   * on a given garment (the Uniform Builder keeps one per layer). Null when
   * there's nothing to charge.
   */
  function bagLine(saved, garment) {
    const map = saved ? (saved instanceof Map ? clone(saved) : new Map(saved.map(([k, v]) => [k, structuredClone(v)]))) : clone(attached);
    return map.size ? payloadOf(map, portableGarment(garment || g)) : null;
  }

  /**
   * Edit a bag line's embroidery in the configurator: its garment and
   * placements go on the stage for this visit only — the page's own
   * attached embroidery, card and previews are untouched — and I'M DONE
   * hands the new payload (or null, all removed) to onDone.
   */
  let external = null;
  function editExternal({ garment: eg, placements = [], trigger, onDone } = {}) {
    if (isOpen) return;
    external = { garment: eg, onDone };
    showGarment(eg);
    open(trigger, new Map(placements.map(([k, v]) => [k, structuredClone(v)])));
  }

  /** Take one charge off a placement. */
  function remove(placeId, part) {
    const d = attached.get(placeId);
    if (!d) return;
    if (part === 'text') { d.line1 = ''; d.line2 = ''; }
    else { d.decoration = null; if (part === 'icon') d.icon = null; else d.logo = null; }
    if (!hasContent(placeId, d)) attached.delete(placeId);
    syncPreviews();
    onChange(clone(attached), { source: 'remove' });
  }

  /* Signed in with nothing attached, the slot offers the saved preferences
     instead of ADD EMBROIDERY; ADD TO PRODUCT attaches them as I'M DONE
     would, EDIT opens the configurator on them. Signing in or out on the
     page tells the hosts to re-decide the slot (source 'account'). */
  const offersPreferences = () => isFigsSignedIn() && attached.size === 0;
  const showsCard = () => attached.size > 0 || isFigsSignedIn();
  function addPreferences() {
    attached = clone(PREFERENCES);
    syncPreviews();
    onChange(clone(attached), { source: 'done' });
  }
  document.addEventListener('figs-account:change', () => {
    syncPreviews();
    onChange(clone(attached), { source: 'account' });
  });

  /* everything off: the card's REMOVE reports it as a removal (the hosts
     swap back to the add-on card and announce it); the lab bar's rebuild
     clears quietly */
  function clear({ source = 'clear' } = {}) {
    attached = new Map();
    syncPreviews();
    onChange(clone(attached), { source });
  }

  /* Put back placements saved from `attached` — the Uniform Builder keeps one
     garment's embroidery per layer and hands it back when that pick reopens */
  function restore(saved) {
    attached = saved ? clone(saved) : new Map();
    syncPreviews();
    onChange(clone(attached), { source: 'restore' });
  }

  /* -- Previews: the stage anywhere, fitted to its host ------------------ */
  const previews = new Set();

  /**
   * Mount a live preview. `fit` is the share of the host's width the garment
   * spans (1 = the whole stage fills it, contained); it's centred both ways.
   */
  function preview(host, { fit = 1 } = {}) {
    host.classList.add('femb-preview');
    host.innerHTML = stageHTML(g);
    const stage = host.querySelector('.femb-stage');
    const slots = Object.fromEntries([...stage.querySelectorAll('.femb-mark')].map((n) => [n.dataset.place, n]));
    const p = {
      host,
      fitNow() {
        const w = host.clientWidth;
        const h = host.clientHeight;
        if (!w || !h) return;
        const s = Math.min((w * fit) / 400, h / 493);
        stage.style.transformOrigin = '0 0';
        stage.style.transform = `translate(${(w - 400 * s) / 2}px, ${(h - 493 * s) / 2}px) scale(${s})`;
      },
      paint(map = attached) { for (const pl of PLACEMENTS) paintMark(slots[pl.id], pl.id, map.get(pl.id)); },
      garment() { stage.querySelector('[data-femb-garment]').innerHTML = garmentFill(g); },
      destroy() { ro.disconnect(); previews.delete(p); },
    };
    const ro = new ResizeObserver(() => p.fitNow());
    ro.observe(host);
    p.fitNow();
    p.paint();
    previews.add(p);
    return p;
  }

  function syncPreviews() {
    previews.forEach((p) => {
      if (!p.host.isConnected) { p.destroy(); return; }
      p.paint();
    });
    cards.forEach((c) => c.update());
  }

  /* -- The spin: the placement screen's 3D garment ------------------------
     treed, 2026-10-18: the first screen of the configurator — nothing open,
     nothing applied — shows the garment turning in 3D on the modal's white;
     every other screen keeps the ghost and its live preview. The spin fades
     in over the ghost and out again on --mo-base, starting front-on, where
     the ghost is. Desktop loops it. A phone turns it once and settles
     front-on, so the landing sample can stitch onto the chest (the
     artifact's mobile). Reduced: the front-on poster, still. */
  const spinVideo = stageEl.querySelector('.femb-stage__spin video');
  let spinOn = false;
  let spinSettled = false;
  function syncSpin(landing) {
    const spin = (external ? external.garment : g).spin;
    const want = Boolean(spin) && landing && isOpen;
    stageEl.classList.toggle('is-spinning', want);
    if (want === spinOn) return;
    spinOn = want;
    if (!want) { spinVideo.pause(); return; }
    if (spinVideo.dataset.src !== spin.src) {
      spinVideo.poster = spin.poster;
      spinVideo.src = spin.src;
      spinVideo.dataset.src = spin.src;
    }
    spinSettled = false;
    spinVideo.loop = !compact.matches;
    spinVideo.currentTime = 0;
    if (reduced()) { spinSettled = true; return; }
    spinVideo.playbackRate = Math.min(4, Math.max(0.25, state.speed));
    spinVideo.play().catch(() => { spinSettled = true; });
  }
  spinVideo.addEventListener('ended', () => {
    spinSettled = true;
    if (isOpen) render();
  });

  /* the configurator's own stage and colourway chip */
  function showGarment(gg) {
    root.querySelector('[data-femb-chip]').style.background = gg.hex;
    root.querySelector('[data-femb-colorway]').textContent = gg.name.toLowerCase();
    stageEl.querySelector('[data-femb-garment]').innerHTML = garmentFill(gg);
    stageEl.querySelector('[data-femb-garment]').setAttribute('aria-label', `${gg.name} scrub top`);
  }

  function setGarment(next) {
    g = next;
    if (!external) showGarment(g);
    previews.forEach((p) => p.garment());
    cards.forEach((c) => c.update());
  }

  /* -- The Embroidery Added card ----------------------------------------- */
  const cards = new Set();
  let cardSeq = 0;

  /* SHOW SUMMARY's rows (Added, Expanded 2667:185680): one per kind, in
     Figma's order (chest text, sleeve text, icon, logo), counted when a kind
     is on more than one placement and its charges summed — "CHEST TEXT
     EMBROIDERY(2) $20". The bag still itemises each charge. */
  const SUMMARY_ORDER = ['Chest Text', 'Sleeve Text', 'Icon', 'Logo'];
  const summaryHTML = () => {
    const kinds = new Map(SUMMARY_ORDER.map((t) => [t, { n: 0, price: 0 }]));
    for (const l of lines()) { const k = kinds.get(l.title); k.n += 1; k.price += l.price; }
    return [...kinds].filter(([, k]) => k.n).map(([title, k]) => `
      <li class="femb-card__line">
        <span>${title} Embroidery${k.n > 1 ? `(${k.n})` : ''}</span><span>${money(k.price)}</span>
      </li>`).join('');
  };

  /**
   * Mount the Embroidery Added card in `host`. It redraws itself whenever
   * what's attached changes; the host decides when it's on screen.
   */
  function card(host, { onEdit = () => open() } = {}) {
    const id = `femb-more-${++cardSeq}`;
    host.innerHTML = `
      <div class="femb-card" data-femb-card>
        <div class="femb-card__summary">
          <div class="femb-card__media" aria-hidden="true" data-femb-card-media></div>
          <div class="femb-card__text">
            <p class="femb-card__title" data-femb-title>Embroidery added</p>
            <p class="femb-card__total">Total: <span data-femb-total></span></p>
            <div class="femb-card__links">
              <button class="femb-card__link femb-card__toggle" type="button" aria-expanded="false" aria-controls="${id}" data-femb-toggle>
                <img class="femb-card__chevron" src="${CHEVRON}" alt="" width="9.188" height="5"><span data-femb-toggle-label>Show summary</span>
              </button>
              <button class="femb-card__link" type="button" data-femb-add-prefs>Add to product</button>
              <button class="femb-card__link" type="button" aria-haspopup="dialog" data-femb-edit>Edit<span class="femb-sr"> embroidery</span></button>
              <button class="femb-card__link" type="button" data-femb-remove-all>Remove<span class="femb-sr"> embroidery</span></button>
            </div>
          </div>
        </div>
        <div class="femb-card__more" id="${id}" hidden data-femb-more>
          <hr class="femb-card__divider">
          <ul class="femb-card__items" aria-label="Embroidery summary" data-femb-items></ul>
        </div>
      </div>`;
    const cardEl = host.querySelector('[data-femb-card]');
    const more = host.querySelector('[data-femb-more]');
    const items = host.querySelector('[data-femb-items]');
    const toggle = host.querySelector('[data-femb-toggle]');
    const label = host.querySelector('[data-femb-toggle-label]');
    const thumb = preview(host.querySelector('[data-femb-card-media]'));

    const title = host.querySelector('[data-femb-title]');
    const text = host.querySelector('.femb-card__text');
    const addPrefs = host.querySelector('[data-femb-add-prefs]');
    const removeAll = host.querySelector('[data-femb-remove-all]');

    /* Two states in one slot: EMBROIDERY ADDED, or — signed in, nothing
       attached — ADD YOUR EMBROIDERY PREFERENCES with ADD TO PRODUCT · EDIT
       (2667:186659). Both are 153 tall, so switching only crossfades the
       text column on --mo-base. */
    const c = {
      el: cardEl,
      update() {
        const prefs = offersPreferences();
        const mode = prefs ? 'prefs' : 'added';
        const changed = cardEl.dataset.mode && cardEl.dataset.mode !== mode;
        cardEl.dataset.mode = mode;
        title.textContent = prefs ? 'Add your embroidery preferences' : 'Embroidery added';
        host.querySelector('[data-femb-total]').textContent = money(sumOf(prefs ? PREFERENCES : attached));
        toggle.hidden = prefs;
        removeAll.hidden = prefs;
        addPrefs.hidden = !prefs;
        items.innerHTML = prefs ? '' : summaryHTML();
        thumb.paint(prefs ? PREFERENCES : attached);
        if (changed && !reduced() && host.offsetParent) {
          move(text, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
        }
      },
    };

    addPrefs.addEventListener('click', () => {
      addPreferences();
      toggle.focus({ preventScroll: true }); // the button it was is gone; SHOW SUMMARY is where it stood
    });

    host.querySelector('[data-femb-edit]').addEventListener('click', (e) => onEdit(e.currentTarget));

    /* SHOW SUMMARY / HIDE SUMMARY. The card's own height animates between
       its two measured sizes (a FLIP of the box, clipped), so the 20px gap,
       the divider and the rows all arrive inside one motion; nothing switches
       on a frame ahead of it, which is what made the recorded expand jump.
       The top padding is 17 in both states (Figma's 16 open + the stroke), so
       the summary row never moves; the open state's 24 bottom lands inside
       the same move. Layout travel is on the standard curve (the tokens'
       "state changes, layout"): --mo-slow out, --mo-base back. Rows rise in
       behind the growing edge on the tight stagger and fade as it folds over
       them. The label and the chevron answer the tap at once. */
    const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';
    const setLabel = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      label.textContent = open ? 'Hide summary' : 'Show summary';
    };
    const setOpen = (open) => {
      setLabel(open);
      cardEl.toggleAttribute('data-open', open);
      more.hidden = !open;
    };
    /* the card's natural height in a state, measured without painting it */
    const heightIn = (open) => {
      const inline = cardEl.style.height;
      const was = isOpen();
      cardEl.style.height = '';
      setOpen(open);
      const h = cardEl.getBoundingClientRect().height;
      setOpen(was);
      cardEl.style.height = inline;
      return h;
    };
    const release = () => requestAnimationFrame(() => {
      // motion commits its end value inline as it finishes; a frame later the
      // card goes back to its natural height so later changes can reflow it
      cardEl.style.height = '';
      cardEl.style.overflow = '';
    });
    let turn = 0;
    async function resize(to, { open, duration }) {
      const mine = ++turn;
      const from = cardEl.getBoundingClientRect().height;
      cardEl.style.overflow = 'clip';
      cardEl.style.height = `${from}px`;
      if (open) setOpen(true);
      await move(cardEl, { height: [from, to] }, { duration, ease: MO.eStandard })?.finished;
      if (mine !== turn) return false; // a newer tap owns the card
      if (!open) setOpen(false);
      release();
      return true;
    }
    /* the fold back: the tap is answered at once, the rows stay (fading)
       until the edge has passed them */
    async function fold() {
      const to = heightIn(false); // measured while the card is still open
      setLabel(false);
      more.hidden = false;
      [...items.children].forEach((row) => move(row, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eStandard }));
      const done = await resize(to, { open: false, duration: MO.base });
      if (done) [...items.children].forEach((row) => { row.style.opacity = ''; });
      return done;
    }

    toggle.addEventListener('click', () => {
      const opening = !isOpen();
      if (reduced()) { setOpen(opening); return; }
      if (!opening) { fold(); return; }
      resize(heightIn(true), { open: true, duration: MO.slow });
      [...items.children].forEach((row, i) => move(row, { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: 0.08 + i * MO.stagTight }));
    });

    /* REMOVE takes all of it off. An open card folds back to its collapsed
       size first, so the page's swap back to the add-on card starts from the
       same height; the card is collapsed again the next time it's shown. */
    host.querySelector('[data-femb-remove-all]').addEventListener('click', async () => {
      if (isOpen() && !reduced()) await fold();
      setOpen(false);
      clear({ source: 'remove' });
    });

    c.update();
    cards.add(c);
    return c;
  }

  instance = {
    open, close, setGarment, preview, card, remove, clear, restore, lines, bagLine, editExternal,
    get isOpen() { return isOpen; },
    get attached() { return clone(attached); },
    has: () => attached.size > 0,
    showsCard, offersPreferences,
    total: () => sumOf(attached),
    /** The bag line's note, the Uniform Builder's form: "Embroidery +$96". */
    note: () => (attached.size ? `Embroidery +${money(sumOf(attached))}` : ''),
  };
  setGarment(g);
  return instance;
}
